/**
 * FilterablePlotly — Plotly figure with auto-generated per-field
 * filter chips above. Mirrors the API of FilterableChart (Vega-Lite)
 * but renders the agent's Plotly `{data, layout, frames}` figure.
 *
 * Field names for the filter bar are derived from the trace `*src`
 * attributes (xsrc, ysrc, labelssrc, valuessrc, marker.colorsrc, ...).
 *
 * @param {Object} chartSpec - Plotly figure {data, layout, frames}.
 * @param {Object} tableData - {columns: string[], rows: any[][]} fresh data.
 * @param {boolean} fill     - Fill its container (dashboard cards).
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Select, Button, Tooltip, Tag } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import PlotlyFigureView from './PlotlyFigureView';

const SRC_KEYS = ['xsrc', 'ysrc', 'labelssrc', 'valuessrc', 'textsrc', 'rsrc', 'thetasrc', 'zsrc'];

function extractPlotlyFieldNames(figure) {
  const out = [];
  const seen = new Set();
  const push = (name) => {
    if (typeof name === 'string' && name && !seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  };
  (figure?.data || []).forEach((trace) => {
    if (!trace || typeof trace !== 'object') return;
    SRC_KEYS.forEach((k) => push(trace[k]));
    if (trace.marker) {
      push(trace.marker.colorsrc);
      push(trace.marker.sizesrc);
    }
  });
  return out;
}

// ---------- inline FilterBar (kept local to avoid a circular import) ----------

function FilterBar({ columns, uniqueValues, filters, onFilterChange, onClearAll }) {
  const activeCount = Object.values(filters).filter(v => v && v.length > 0).length;
  return (
    <div style={{ marginBottom: 8, padding: 8, background: '#fafafa', borderRadius: 4, border: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
        <FilterOutlined style={{ color: '#1890ff' }} />
        <span style={{ fontSize: 12, fontWeight: 600 }}>Chart filters</span>
        {activeCount > 0 && <Tag color="blue">{activeCount} active</Tag>}
        {activeCount > 0 && (
          <Tooltip title="Clear all chart filters">
            <Button size="small" icon={<ClearOutlined />} onClick={onClearAll}>Clear</Button>
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {columns.map((col, idx) => (
          <Select
            key={col}
            mode="multiple"
            allowClear
            placeholder={col}
            style={{ minWidth: 160 }}
            size="small"
            value={filters[idx] || []}
            onChange={(vals) => onFilterChange(idx, vals)}
            options={(uniqueValues[idx] || []).map(v => ({ value: v, label: v }))}
            maxTagCount="responsive"
          />
        ))}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------

export default function FilterablePlotly({ chartSpec, tableData, fill = false }) {
  const [filters, setFilters] = useState({});

  const parsedFigure = useMemo(() => {
    if (!chartSpec) return null;
    try {
      return typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
    } catch {
      return null;
    }
  }, [chartSpec]);

  const fieldNames = useMemo(() => extractPlotlyFieldNames(parsedFigure), [parsedFigure]);

  // Plotly *src field names typically arrive lowercase from the chart spec
  // while Snowflake column headers are uppercase. Resolve case-insensitively.
  const resolveColumn = useCallback((field) => {
    if (!field || !tableData?.columns) return null;
    const lower = String(field).toLowerCase();
    for (let i = 0; i < tableData.columns.length; i += 1) {
      if (String(tableData.columns[i]).toLowerCase() === lower) {
        return { name: tableData.columns[i], index: i };
      }
    }
    return null;
  }, [tableData]);

  const uniqueValues = useMemo(() => {
    const opts = {};
    fieldNames.forEach((field, idx) => {
      const resolved = resolveColumn(field);
      if (!resolved) { opts[idx] = []; return; }
      const set = new Set();
      for (const row of (tableData?.rows || [])) {
        if (set.size >= 100) break;
        const cell = row[resolved.index];
        set.add(cell != null ? String(cell) : '(null)');
      }
      opts[idx] = [...set].sort((a, b) => {
        const na = Number(a); const nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
    });
    return opts;
  }, [fieldNames, tableData, resolveColumn]);

  // Apply chip filters to tableData → produce filtered tableData passed to PlotlyFigureView.
  const filteredTableData = useMemo(() => {
    if (!tableData?.columns || !tableData?.rows) return tableData;
    const active = Object.entries(filters).filter(([_, v]) => v && v.length > 0);
    if (active.length === 0) return tableData;
    const rows = tableData.rows.filter((row) => (
      active.every(([fIdx, values]) => {
        const field = fieldNames[Number(fIdx)];
        const resolved = resolveColumn(field);
        if (!resolved) return true;
        const cell = row[resolved.index] != null ? String(row[resolved.index]) : '(null)';
        return values.includes(cell);
      })
    ));
    return { ...tableData, rows };
  }, [tableData, filters, fieldNames, resolveColumn]);

  const handleFilterChange = useCallback((idx, values) => {
    setFilters((prev) => ({ ...prev, [idx]: values }));
  }, []);
  const handleClearAll = useCallback(() => setFilters({}), []);

  return (
    <div style={fill
      ? { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      : { background: '#fff', padding: 12, borderRadius: 4, overflow: 'auto' }
    }>
      {fieldNames.length > 0 && (tableData?.rows?.length || 0) > 0 && (
        <FilterBar
          columns={fieldNames}
          uniqueValues={uniqueValues}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAll}
        />
      )}
      <div style={fill ? { flex: 1, minHeight: 0 } : { minHeight: 300 }}>
        <PlotlyFigureView figure={parsedFigure} tableData={filteredTableData} fill={fill} />
      </div>
    </div>
  );
}
